import { IQuote } from "../../../../models/QuoteData";

// How the vlm3 quotes page groups and orders quotes; shared by the page and its App Insights view events.

// Quick quotes come back with no quoteID and the same quoteNumber ("1"), so neither identifies a quote on its own.
export function quoteKey(quote: IQuote, index: number) {
  return `${quote.quoteID ?? quote.quoteNumber ?? ""}-${quote.providerName}-${quote.productName}-${quote.annuityAmountPA}-${index}`;
}

function sameQuote(a: IQuote, b: IQuote) {
  return (
    a.providerName === b.providerName &&
    a.productName === b.productName &&
    String(a.annuityAmountPA) === String(b.annuityAmountPA) &&
    String(a.termYears ?? "") === String(b.termYears ?? "")
  );
}

export function isFixedTermQuote(quote: IQuote) {
  const name = `${quote.productName ?? ""} ${quote.productType ?? ""}`.toLowerCase();
  const termYears = Number(quote.termYears ?? 0);
  return name.includes("fixed") || (termYears > 0 && termYears < 999);
}

export function getSortedQuotes(quotes: IQuote[]) {
  return [...quotes].sort((a, b) => Number(b.annuityAmountPA ?? 0) - Number(a.annuityAmountPA ?? 0));
}

export function getQuoteGroups(quotes: IQuote[]) {
  const lifetime = getSortedQuotes(quotes.filter((quote) => !isFixedTermQuote(quote)));
  const fixed = getSortedQuotes(quotes.filter(isFixedTermQuote));
  const fallback = getSortedQuotes(quotes);

  return [
    { key: "lifetime", title: "Best Lifetime Annuity Quotes", quotes: lifetime.length ? lifetime : fallback },
    { key: "fixed", title: "Best Fixed Term Annuity Quotes", quotes: fixed.length ? fixed : fallback },
  ].filter((group) => group.quotes.length > 0);
}

/** Which quote was opened, as the quotes page shows it: its group and 1-based place within that group. */
export function quoteDetailViewProperties({
  quotes,
  selectedQuote,
}: {
  quotes: IQuote[];
  selectedQuote: IQuote | null;
}): Record<string, string> {
  if (!selectedQuote) return {};
  const fixed = isFixedTermQuote(selectedQuote);
  const group = getQuoteGroups(quotes).find((candidate) => candidate.key === (fixed ? "fixed" : "lifetime"));
  const position = (group?.quotes ?? []).findIndex((quote) => sameQuote(quote, selectedQuote));
  return {
    provider: selectedQuote.providerName ?? "",
    quote_type: fixed ? "fixed" : "lifetime",
    position: position >= 0 ? String(position + 1) : "",
  };
}
